import { useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';

function canReceiveFocus(element: HTMLElement | null | undefined): element is HTMLElement {
  if (!element?.isConnected || element.matches(':disabled, [aria-disabled="true"]')) return false;
  // Base UI 1.9 keeps the page aria-hidden until after finalFocus resolves.
  // The destination is selected here and focused after that marker clears.
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    if (node.hidden || node.inert) return false;
    const style = getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
  }
  return true;
}

export function resolveReturnFocus(...preferred: (HTMLElement | null | undefined)[]): HTMLElement | undefined {
  const candidates = [
    ...preferred,
    ...document.querySelectorAll<HTMLElement>('[data-shared-button], a[href]'),
  ];
  return candidates.find(canReceiveFocus);
}

function resolveWithin(scope: HTMLElement, ...preferred: (HTMLElement | null | undefined)[]): HTMLElement | undefined {
  return [...preferred, ...scope.querySelectorAll<HTMLElement>('[data-shared-button], a[href]')]
    .find((candidate): candidate is HTMLElement => candidate != null && scope.contains(candidate) && canReceiveFocus(candidate));
}

/** Capture the opener before a nested layer mounts, then choose a live return target on close. */
export function useReturnFocusTarget(): () => HTMLElement | undefined {
  const opener = useRef<HTMLElement | null>(
    document.activeElement instanceof HTMLElement ? document.activeElement : null,
  );
  const parentDialog = useRef(opener.current?.closest<HTMLElement>('[role="dialog"], [role="alertdialog"]') ?? null);
  useEffect(() => () => {
    // A successful nested action can remove its opener in the same render as the
    // nested dialog. Wait until Base UI restores the surviving modal's inertness.
    requestAnimationFrame(() => {
      const parent = parentDialog.current;
      const target = parent?.isConnected
        ? resolveWithin(parent, opener.current)
        : resolveReturnFocus(opener.current);
      target?.focus();
    });
  }, []);
  return () => {
    const parent = parentDialog.current;
    return parent?.isConnected
      ? resolveWithin(parent, opener.current)
      : resolveReturnFocus(opener.current);
  };
}

/** Keep focus live when an async action removes its originating row. */
export function removeRowWithReturnFocus(source: HTMLElement, remove: () => void): void {
  const row = source.closest('li, tr, [data-focus-removal-boundary]');
  const focusWasInside = row?.contains(document.activeElement) ?? false;
  flushSync(remove);
  if (focusWasInside) resolveReturnFocus()?.focus();
}
